import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import tinycolor from 'tinycolor2';

import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';

import { SnCurrentOrganization } from '@lib/auth';
import {
  GoogleMap,
  GoogleMapMarker,
  GoogleMapMarkerType,
  GoogleMapPopup,
} from '@lib/geo/google/map';
import { THEME } from '@lib/ui/theme';

import { SnLprRead } from '../../api';
import { PlateInfoPopupComponent } from '../plate-info-popup/plate-info-popup.component';

@Component({
  selector: 'fs-result-map-heatmap',
  templateUrl: './result-map-heatmap.component.html',
  styleUrls: ['./result-map-heatmap.component.scss'],
})
export class ResultMapHeatmapComponent implements OnDestroy, OnInit {
  get organization(): any {
    return this._currentOrganization.settings.geometry.coordinates;
  }

  public map: GoogleMap;
  public heatmap: google.maps.visualization.HeatmapLayer;
  public heatmapActive: boolean = false;

  public carMarkers: GoogleMapMarker[] = [];
  public markersActive: boolean = true;

  private _polyline: google.maps.Polyline;
  private readonly _destroy$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public results: any,
    private readonly _currentOrganization: SnCurrentOrganization,
    private readonly _cd: ChangeDetectorRef,
  ) {}

  public ngOnInit(): void {}

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public onMapInit(googleMap: GoogleMap): void {
    this.map = googleMap;
    if (this.map) {
      this.map.setCenter(this.results[0].geometry.coordinates);
      this._createBreadcrumbs();
      if (this.heatmapActive) {
        this.heatmap.setMap(null);
        this.heatmapActive = false;
      }
    }
  }

  public toggleHeatmap(): void {
    if (!this.heatmap) {
      this._createHeatmap();

      if (this._polyline) {
        this._polyline.setMap(null);
        this.carMarkers.forEach((marker) => {
          marker.remove();
        });
        this.markersActive = false;
        this.carMarkers = [];
      }

      return;
    }

    if (this._polyline) {
      this._polyline.setMap(null);
      this.carMarkers.forEach((marker) => {
        marker.remove();
      });
      this.markersActive = false;
      this.carMarkers = [];
    }

    this._setHeatmap();
  }

  public toggleMarkers(): void {
    if (!this.carMarkers.length) {
      this._createBreadcrumbs();

      if (this.heatmap) {
        this.heatmap.setMap(null);
        this.heatmapActive = false;
      }
      return;
    }

    this.carMarkers.forEach((marker: GoogleMapMarker) => {
      marker.remove();
    });

    if (this._polyline) {
      this._polyline.setMap(null);
    }

    this.carMarkers = [];
    this.markersActive = false;
    this._setHeatmap();
  }

  private _createHeatmap(): void {
    const heatmapCoordinates = [];
    this.results.forEach((plate) => {
      heatmapCoordinates.push(
        new google.maps.LatLng(plate.geometry.coordinates[1], plate.geometry.coordinates[0]),
      );
    });
    this.heatmap = new google.maps.visualization.HeatmapLayer({
      data: heatmapCoordinates,
      radius: 12,
    });
    this.heatmap.setMap(this.map.mapInstance);
    this.heatmapActive = true;
  }

  private _setHeatmap(): void {
    const map = this.heatmap.getMap();

    if (map) {
      this.heatmap.setMap(null);
      this.heatmapActive = false;

      this._createBreadcrumbs();
      return;
    }

    if (!map) {
      this.heatmap.setMap(this.map.mapInstance);
      this.heatmapActive = true;

      if (this._polyline) {
        this._polyline.setMap(null);
        this.carMarkers.forEach((marker) => {
          marker.remove();
        });
        this.markersActive = false;
      }
    }
  }

  private _setMarker(plate: SnLprRead, color?: string, scale?: number): GoogleMapMarker {
    return new GoogleMapMarker({
      id: `plate-${plate.id}`,
      type: GoogleMapMarkerType.CIRCLE,
      lonLat: {
        lng: plate.geometry.coordinates[0],
        lat: plate.geometry.coordinates[1],
      },
      title: plate.plate,
      style: {
        scale,
        strokeOpacity: 1,
        fillOpacity: 1,
        fillColor: scale === 1 ? color : THEME.success,
        strokeWeight: scale === 0.3 ? 0.5 : 1,
      },
      icon: {
        fontSet: 'fa',
        fontIcon: 'fa-car',
      },
    });
  }

  private _openPopup(plate: SnLprRead): void {
    const popup = new GoogleMapPopup(PlateInfoPopupComponent, {
      data: {
        plate,
      },
      lonLat: plate.geometry.coordinates,
    });
    popup.open(this.map);
  }

  private _createBreadcrumbs(): void {
    if (this.carMarkers) {
      this.carMarkers.forEach((marker) => {
        marker.remove();
      });

      this.carMarkers = [];
    }
    if (this._polyline) {
      this._polyline.setMap(null);
      this._polyline = null;
    }
    const linePoints = this.results.map(
      (plate) =>
        new google.maps.LatLng(plate.geometry.coordinates[1], plate.geometry.coordinates[0]),
    );
    const totalCount = this.results.length;
    const plates = [...this.results];
    plates.reverse().forEach((plate: SnLprRead, index) => {
      const color = this._getMarkerColor(totalCount, index);
      const scale = index === totalCount - 1 ? 1.4 : 1;
      const marker = this._setMarker(plate, color, scale);
      this.carMarkers.push(marker);
      marker.addTo(this.map);
      marker
        .on('click')
        .pipe(takeUntil(this._destroy$))
        .subscribe(() => {
          this._openPopup(plate);
          this._cd.detectChanges();
        });
    });
    const lineSymbol = {
      path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
      fillOpacity: 1,
      strokeOpacity: 1,
      strokeWeight: 2,
      fillColor: '#fff',
      strokeColor: 'black',
      scale: 4,
    };

    this._polyline = new google.maps.Polyline({
      path: linePoints.reverse(),
      strokeOpacity: 1,
      strokeColor: THEME.success,
      strokeWeight: 1,
      draggable: false,
      icons: [
        {
          icon: lineSymbol,
          offset: '0',
          repeat: '100px',
        },
      ],
    });
    this._polyline.setMap(this.map.mapInstance);
    this.markersActive = true;
  }

  private _getMarkerColor(totalCount: number, currentIndex: number): string {
    const greenColor = THEME.success;
    const desaturateAmount = Math.round(100 / (totalCount - 1));

    const desaturateBy = 100 - currentIndex * desaturateAmount;
    return tinycolor(greenColor).desaturate(desaturateBy).toRgbString();
  }
}
